import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Loader2, Archive, RotateCcw } from 'lucide-react';
import { calendarApi, projectsApi } from '../api';
import { EVENT_TYPE_OPTIONS, EVENT_STATUS_OPTIONS } from '../calendarConstants';

const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';
const selectCls = 'w-full bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
import { isNA, naToNull } from '../constants';
import { PhoneInput } from './PhoneInput';

const emptyForm = (date, type) => ({
  title: '', event_type: type || 'appointment', status: 'scheduled', description: '',
  project_id: '', contact_name: '', contact_phone: '', contact_email: '', location: '',
  start_date: date || '', end_date: '', is_all_day: true, start_time: '', end_time: '',
  internal_notes: '',
});

const Field = ({ label, children, full, help }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="text-sm text-gray-300 mb-1.5 block font-medium">{label}</label>
    {children}
    {help && <p className="text-xs text-gray-500 mt-1">{help}</p>}
  </div>
);

export const EventFormDialog = ({ open, onOpenChange, mode = 'create', event, defaultDate, defaultType, onSaved }) => {
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [confirmArchive, setConfirmArchive] = useState(false);

  useEffect(() => {
    if (!open) return;
    projectsApi.list({ archived: false, sort: 'name_az' }).then((r) => setProjects(r.data)).catch(() => {});
    if (mode === 'edit' && event) {
      setForm({
        ...emptyForm(), ...event,
        project_id: event.project_id || '', description: event.description || '',
        contact_name: event.contact_name || '', contact_phone: event.contact_phone || '',
        contact_email: event.contact_email || '', location: event.location || '',
        end_date: event.end_date || '', start_time: event.start_time || '', end_time: event.end_time || '',
        internal_notes: event.internal_notes || '',
      });
    } else {
      setForm(emptyForm(defaultDate, defaultType));
    }
  }, [open, mode, event, defaultDate, defaultType]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.title.trim()) { toast.error('Event title is required'); return false; }
    if (!form.start_date) { toast.error('Start date is required'); return false; }
    if (form.contact_email && !isNA(form.contact_email) && !EMAIL_RE.test(form.contact_email)) { toast.error('Invalid contact email'); return false; }
    if (form.end_date && form.end_date < form.start_date) { toast.error('End date cannot be before start date'); return false; }
    if (!form.is_all_day && !form.start_time) { toast.error('Start time is required for a timed event'); return false; }
    if (!form.is_all_day && form.start_time && form.end_time && (!form.end_date || form.end_date === form.start_date) && form.end_time < form.start_time) {
      toast.error('End time cannot be before start time'); return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (submitting || !validate()) return;
    setSubmitting(true);
    const payload = {
      title: form.title.trim(), event_type: form.event_type, status: form.status,
      description: form.description || null, internal_notes: form.internal_notes || null,
      project_id: form.project_id || null,
      contact_name: form.contact_name || null, contact_phone: form.contact_phone || null,
      contact_email: naToNull(form.contact_email), location: form.location || null,
      start_date: form.start_date, end_date: form.end_date || null,
      is_all_day: form.is_all_day,
      start_time: form.is_all_day ? null : form.start_time || null,
      end_time: form.is_all_day ? null : form.end_time || null,
      timezone: 'America/New_York',
    };
    try {
      const res = mode === 'edit'
        ? await calendarApi.updateEvent(event.id, payload)
        : await calendarApi.createEvent(payload);
      toast.success(mode === 'edit' ? 'Event updated' : 'Event created');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save event');
    } finally { setSubmitting(false); }
  };

  const doArchive = async () => {
    try { await calendarApi.archiveEvent(event.id); toast.success('Event archived'); setConfirmArchive(false); onOpenChange(false); onSaved?.({}); }
    catch { toast.error('Failed to archive'); }
  };
  const doRestore = async () => {
    try { await calendarApi.restoreEvent(event.id); toast.success('Event restored'); onOpenChange(false); onSaved?.({}); }
    catch { toast.error('Failed to restore'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            {mode === 'edit' ? 'Event' : 'New Event'}
            {mode === 'edit' && event?.is_archived && (
              <span className="text-[10px] uppercase text-gray-400 border border-gray-600 rounded px-1.5 py-0.5">Archived</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Manual calendar event. Title, type and start date are required.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Field label="Title *" full>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} className={inputCls} data-testid="ef-title" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Event Type">
              <select value={form.event_type} onChange={(e) => set('event_type', e.target.value)} className={selectCls} data-testid="ef-type">
                {EVENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => set('status', e.target.value)} className={selectCls} data-testid="ef-status">
                {EVENT_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Project (optional)" full>
              <select value={form.project_id} onChange={(e) => set('project_id', e.target.value)} className={selectCls} data-testid="ef-project">
                <option value="">General / No Project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Description" full>
            <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} className={inputCls} data-testid="ef-description" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Contact Name"><Input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className={inputCls} data-testid="ef-contact-name" /></Field>
            <Field label="Contact Phone"><PhoneInput value={form.contact_phone} onChange={(v) => set('contact_phone', v)} className={inputCls} data-testid="ef-contact-phone" /></Field>
            <Field label="Contact Email"><Input value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} className={inputCls} data-testid="ef-contact-email" /></Field>
          </div>
          <Field label="Location" full><Input value={form.location} onChange={(e) => set('location', e.target.value)} className={inputCls} data-testid="ef-location" /></Field>

          {/* Schedule */}
          <div className="border-t border-gray-800 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Start Date *"><Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} className={inputCls} data-testid="ef-start-date" /></Field>
            <Field label="End Date (optional)"><Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} className={inputCls} data-testid="ef-end-date" /></Field>
            <label className="flex items-center gap-2 text-sm text-gray-300 md:col-span-2 cursor-pointer">
              <input type="checkbox" checked={form.is_all_day} onChange={(e) => set('is_all_day', e.target.checked)} className="accent-[#D4AF37] h-4 w-4" data-testid="ef-all-day" />
              All-day event
            </label>
            {!form.is_all_day && (
              <>
                <Field label="Start Time"><Input type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} className={inputCls} data-testid="ef-start-time" /></Field>
                <Field label="End Time"><Input type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} className={inputCls} data-testid="ef-end-time" /></Field>
              </>
            )}
          </div>

          <Field label="Internal Notes (internal only)" full>
            <Textarea value={form.internal_notes} onChange={(e) => set('internal_notes', e.target.value)} rows={2} className={inputCls} data-testid="ef-internal-notes" />
          </Field>
        </div>

        <div className="flex flex-wrap gap-3 justify-between border-t border-gray-800 pt-4 sticky bottom-0 bg-[#111111]">
          <div>
            {mode === 'edit' && event && (
              event.is_archived
                ? <Button onClick={doRestore} variant="outline" className="border-gray-700 text-gray-300" data-testid="ef-restore"><RotateCcw size={16} /> Restore</Button>
                : <Button onClick={() => setConfirmArchive(true)} variant="outline" className="border-gray-700 text-gray-300 hover:text-red-400 hover:border-red-500" data-testid="ef-archive"><Archive size={16} /> Archive</Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} className="border-gray-700 text-gray-300" data-testid="ef-cancel">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="ef-submit">
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {mode === 'edit' ? 'Save Changes' : 'Create Event'}
            </Button>
          </div>
        </div>

        <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
          <AlertDialogContent className="bg-[#111111] border-gray-800 text-white" data-testid="event-archive-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Archive this event?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                The event will be removed from the normal Calendar but its information will be preserved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border-gray-700 text-gray-300 hover:bg-[#1A1A1A]">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={doArchive} className="bg-red-600 text-white hover:bg-red-700" data-testid="event-archive-confirm-btn">Archive</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
